import React, { useMemo, useState, useEffect, useRef } from 'react'; 
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Grid,
  Paper,
  useMediaQuery,
  useTheme,
  Tooltip
} from '@mui/material'; 
import { 
  Close, 
  EmojiEvents, 
  OpenInNew, 
  CalendarMonth, 
  Height, 
  Public, 
  Flag, 
  Groups, 
  EmojiEvents as Trophy,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Add,
  Remove
} from '@mui/icons-material';
import { DndProvider, useDrag, useDrop } from 'react-dnd'; 
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';

const DraggableListBox = ({ position, players, sx, onPlayerClick, onMove, onAddPlayer, onRemovePlayer, canAddMore, canRemove, isMobile, isDesktop, mediaUrl, containerRef }) => {
  const ref = useRef(null);
  
  const [{ isDragging }, drag] = useDrag({
    type: 'position-box',
    item: () => {
      const rect = ref.current?.getBoundingClientRect();
      const containerRect = containerRef.current?.getBoundingClientRect();
      return {
        position,
        width: rect.width,
        height: rect.height,
        initialOffset: {
          x: rect.left - containerRect.left,
          y: rect.top - containerRect.top
        }
      };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !isMobile,
  });

  drag(ref);

  const getRankColor = (rank) => {
    switch(rank) {
      case 1: return '#FFD700';
      case 2: return '#C0C0C0';
      case 3: return '#CD7F32';
      default: return '#E0E0E0';
    }
  };

  const getTrendIcon = (indicator) => {
    const lowerIndicator = indicator?.toLowerCase();
    if (lowerIndicator?.includes('high')) {
      return <TrendingUp color="success" sx={{ fontSize: '0.8rem' }} />;
    } else if (lowerIndicator?.includes('low')) {
      return <TrendingDown color="error" sx={{ fontSize: '0.8rem' }} />;
    } else if (lowerIndicator?.includes('stable')) {
      return <TrendingFlat color="action" sx={{ fontSize: '0.8rem' }} />;
    }
    return null;
  };

  const joinWithDash = (...items) => items.filter(Boolean).join(' - ');
  
  const getFirstName = (fullName) => {
    if (!fullName) return '';
    const parts = fullName.split(' ');
    return parts[0];
  };

  const splitName = (fullName) => {
    if (!fullName) return { firstName: '', lastName: '' };
    const parts = fullName.split(' ');
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ');
    return { firstName, lastName };
  };

  const getPlayerTooltip = (player) => {
    const lines = [];
    
    if (player.poste || player.annee_naissance || player.taille) {
      lines.push(joinWithDash(
        player.poste,
        player.annee_naissance,
        player.taille ? `${player.taille}cm` : null
      ));
    }
    
    if (player.nationalite) {
      lines.push(player.nationalite + (player.passport ? ` (${player.passport})` : ''));
    }
    
    // Add competitions_detail if available, otherwise fallback to old fields
    if (player.competitions_detail && player.competitions_detail.length > 0) {
      // Group competitions by team
      const equipesMap = new Map();
      player.competitions_detail.forEach(comp => {
        if (!equipesMap.has(comp.equipe_id)) {
          equipesMap.set(comp.equipe_id, {
            equipe_nom: comp.equipe_nom,
            competitions: []
          });
        }
        equipesMap.get(comp.equipe_id).competitions.push(comp.competition_nom);
      });

      // Format as: Team1 (Comp1, Comp2)
      const teamsStr = Array.from(equipesMap.values())
        .map(equipe => `${equipe.equipe_nom} (${equipe.competitions.join(', ')})`)
        .join(' - ');

      if (teamsStr) lines.push(teamsStr);
    } else if (player.team || player.league_1 || player.league_2 || player.competicion_europa) {
      // Fallback to old fields
      lines.push(joinWithDash(
        player.team,
        [player.league_1, player.league_2, player.competicion_europa]
          .filter(Boolean)
          .join(' - ')
      ));
    }
    
    if (player.forme_indicator) {
      lines.push(`Forma: ${player.forme_indicator}`);
    }
    
    if (player.notes) {
      lines.push(player.notes);
    }
    
    return lines.join('\n');
  };

  if (isMobile) {
    return (
      <Box
        ref={ref}
        sx={{
          position: 'absolute',
          bgcolor: 'rgba(255,255,255,0.95)',
          borderRadius: 1,
          p: 0.5,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          minWidth: 'fit-content',
          maxWidth: '140px',
          width: 'auto',
          border: '1px solid',
          borderColor: 'rgba(0,0,0,0.1)',
          ...sx
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 'bold',
            fontSize: '0.65rem',
            color: '#333',
            textTransform: 'uppercase',
            textAlign: 'center',
            display: 'block',
            mb: 0.5,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'break-word'
          }}
        >
          {position}
        </Typography>
        
        {players.length > 0 ? (
          players.map((p, index) => (
            <Box
              key={p.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                mb: 0.5,
                p: 0.3,
                cursor: 'pointer',
                borderRadius: 0.5,
                bgcolor: index < 3 ? `${getRankColor(index + 1)}15` : 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.05)'
                }
              }}
              onClick={() => onPlayerClick(p)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                {index < 3 ? (
                  <EmojiEvents
                    sx={{
                      fontSize: '1.1rem',
                      color: getRankColor(index + 1),
                      minWidth: 16
                    }}
                  />
                ) : (
                  <Box sx={{ minWidth: 16, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#666' }}>
                      {index + 1}
                    </Typography>
                  </Box>
                )}

                <Avatar
                  src={`${mediaUrl}/photos/${p.id}.png`}
                  alt={p.nom}
                  sx={{
                    width: 24,
                    height: 24,
                    border: index < 3 ? `1px solid ${getRankColor(index + 1)}` : 'none'
                  }}
                />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: '0.65rem',
                      lineHeight: 1,
                      fontWeight: index < 3 ? 600 : 400
                    }}
                  >
                    {getFirstName(p.nom)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      color: '#666',
                      lineHeight: 1
                    }}
                    noWrap
                  >
                    {p.poste}
                  </Typography>
                </Box>

              </Box>
            </Box>
          ))
        ) : (
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.5rem',
              color: '#999',
              fontStyle: 'italic',
              textAlign: 'center',
              display: 'block'
            }}
          >
            Sin jugadores
          </Typography>
        )}
      </Box>
    );
  }
  
  if (isDesktop) {
    return (
      <Box
        ref={ref}
        sx={{
          position: 'absolute',
          bgcolor: 'rgba(255,255,255,0.95)',
          borderRadius: 2,
          p: 1,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          minWidth: 'fit-content',
          maxWidth: '220px',
          width: 'auto',
          border: '2px solid',
          borderColor: 'rgba(0,0,0,0.1)',
          backdropFilter: 'blur(10px)',
          opacity: isDragging ? 0.5 : 1,
          cursor: !isMobile ? 'move' : 'default',
          transition: 'border-color 0.2s',
          zIndex: isDragging ? 1000 : 10,
          ...sx
        }}
      >
        <Box sx={{ 
          borderBottom: '2px solid rgba(0,0,0,0.1)', 
          pb: 0.5, 
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 'bold',
              fontSize: '0.9rem',
              color: '#333',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              flex: 1
            }}
          >
            {position}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {canAddMore && (
              <IconButton 
                size="small" 
                onClick={onAddPlayer}
                sx={{ 
                  width: 20, 
                  height: 20,
                  '&:hover': { bgcolor: 'success.light' }
                }}
              >
                <Add sx={{ fontSize: '1rem' }} />
              </IconButton>
            )}
            {canRemove && (
              <IconButton 
                size="small" 
                onClick={onRemovePlayer}
                sx={{ 
                  width: 20, 
                  height: 20,
                  '&:hover': { bgcolor: 'error.light' }
                }}
              >
                <Remove sx={{ fontSize: '1rem' }} />
              </IconButton>
            )}
          </Box>
        </Box>
        
        {players.length > 0 ? (
          players.map((p, index) => (
            <Box
              key={p.id}
              sx={{
                mb: 0.75,
                p: 0.5,
                borderRadius: 1,
                bgcolor: index < 3 ? `${getRankColor(index + 1)}20` : 'rgba(0,0,0,0.02)',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                border: `1px solid ${index < 3 ? getRankColor(index + 1) : 'transparent'}40`,
                '&:hover': {
                  bgcolor: index < 3 ? `${getRankColor(index + 1)}30` : 'rgba(0,0,0,0.08)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                }
              }}
              onClick={() => onPlayerClick(p)}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: 28
                }}>
                  <Avatar
                    src={`${mediaUrl}/photos/${p.id}.png`}
                    alt={p.nom}
                    sx={{
                      width: 36,
                      height: 36,
                      border: index < 3 ? `2px solid ${getRankColor(index + 1)}` : '1px solid #ddd',
                      mb: 0.2
                    }}
                  />
                  {index < 3 ? (
                    <EmojiEvents
                      sx={{
                        fontSize: '1.3rem',
                        color: getRankColor(index + 1)
                      }}
                    />
                  ) : (
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#666' }}>
                      {index + 1}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: index < 3 ? 600 : 500,
                          color: '#333',
                          lineHeight: 1
                        }}
                        noWrap
                      >
                        {splitName(p.nom).firstName}
                        {splitName(p.nom).lastName && ` ${splitName(p.nom).lastName}`}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          color: '#666',
                          lineHeight: 1.2,
                          mt: 0.2
                        }}
                        noWrap
                      >
                        {p.poste} {p.annee_naissance ? `• ${p.annee_naissance}` : ''} {p.taille ? `• ${p.taille}cm` : ''}
                      </Typography>
                      {(() => {
                        // Display team from competitions_detail or fallback to old team field
                        let teamDisplay = null;
                        if (p.competitions_detail && p.competitions_detail.length > 0) {
                          // Get unique team names
                          const uniqueTeams = [...new Set(p.competitions_detail.map(c => c.equipe_nom))];
                          teamDisplay = uniqueTeams.join(', ');
                        } else if (p.team) {
                          teamDisplay = p.team;
                        }

                        return teamDisplay ? (
                          <Typography
                            sx={{
                              fontSize: '0.7rem',
                              color: '#666',
                              lineHeight: 1.2
                            }}
                            noWrap
                          >
                            {teamDisplay}
                          </Typography>
                        ) : null;
                      })()}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))
        ) : (
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.7rem',
              color: '#999',
              fontStyle: 'italic',
              textAlign: 'center',
              display: 'block'
            }}
          >
            Sin jugadores
          </Typography>
        )}
      </Box>
    );
  }
  
  // Tablet version
  return (
    <Box
      ref={ref}
      sx={{
        position: 'absolute',
        bgcolor: 'rgba(255,255,255,0.95)',
        borderRadius: 2,
        p: 0.75,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        minWidth: 'fit-content',
        maxWidth: '180px',
        width: 'auto',
        border: '2px solid',
        borderColor: 'rgba(0,0,0,0.1)',
        opacity: isDragging ? 0.5 : 1,
        cursor: !isMobile ? 'move' : 'default',
        transition: 'border-color 0.2s',
        zIndex: isDragging ? 1000 : 10,
        ...sx
      }}
    >
      <Box sx={{ 
        borderBottom: '2px solid rgba(0,0,0,0.1)', 
        pb: 0.5, 
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 'bold',
            fontSize: '0.85rem',
            color: '#333',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            flex: 1
          }}
        >
          {position}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          {canAddMore && (
            <IconButton 
              size="small" 
              onClick={onAddPlayer}
              sx={{ 
                width: 18, 
                height: 18,
                '&:hover': { bgcolor: 'success.light' }
              }}
            >
              <Add sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          )}
          {canRemove && (
            <IconButton 
              size="small" 
              onClick={onRemovePlayer}
              sx={{ 
                width: 18, 
                height: 18,
                '&:hover': { bgcolor: 'error.light' }
              }}
            >
              <Remove sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          )}
        </Box>
      </Box>
      
      {players.length > 0 ? (
        players.map((p, index) => (
          <Box
            key={p.id}
            sx={{
              mb: 0.5,
              p: 0.4,
              borderRadius: 1,
              bgcolor: index < 3 ? `${getRankColor(index + 1)}20` : 'transparent',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.1)',
                transform: 'translateX(2px)'
              }
            }}
            onClick={() => onPlayerClick(p)}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minWidth: 26,
                mr: 0.4
              }}>
                <Avatar
                  src={`${mediaUrl}/photos/${p.id}.png`}
                  alt={p.nom}
                  sx={{
                    width: 28,
                    height: 28,
                    border: index < 3 ? `1.5px solid ${getRankColor(index + 1)}` : 'none',
                    mb: 0.2
                  }}
                />
                {index < 3 ? (
                  <EmojiEvents
                    sx={{
                      fontSize: '1.15rem',
                      color: getRankColor(index + 1)
                    }}
                  />
                ) : (
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#666' }}>
                    {index + 1}
                  </Typography>
                )}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        lineHeight: 1,
                        fontWeight: index < 3 ? 600 : 400,
                        color: '#333'
                      }}
                      noWrap
                    >
                      {splitName(p.nom).firstName}
                      {splitName(p.nom).lastName && ` ${splitName(p.nom).lastName}`}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.68rem',
                        color: '#666',
                        lineHeight: 1.1,
                        mt: 0.1
                      }}
                      noWrap
                    >
                      {p.poste} {p.annee_naissance ? `• ${p.annee_naissance}` : ''}
                    </Typography>
                    {(() => {
                      // Display team from competitions_detail or fallback to old team field
                      let teamDisplay = null;
                      if (p.competitions_detail && p.competitions_detail.length > 0) {
                        // Get unique team names
                        const uniqueTeams = [...new Set(p.competitions_detail.map(c => c.equipe_nom))];
                        teamDisplay = uniqueTeams.join(', ');
                      } else if (p.team) {
                        teamDisplay = p.team;
                      }

                      return teamDisplay ? (
                        <Typography
                          sx={{
                            fontSize: '0.68rem',
                            color: '#666',
                            lineHeight: 1.1
                          }}
                          noWrap
                        >
                          {teamDisplay}
                        </Typography>
                      ) : null;
                    })()}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        ))
      ) : (
        <Typography 
          variant="caption" 
          sx={{ 
            fontSize: '0.7rem',
            color: '#999',
            fontStyle: 'italic',
            textAlign: 'center',
            display: 'block'
          }}
        >
          Sin jugadores
        </Typography>
      )}
    </Box>
  );
};

const BasketballCourt = ({ boards, mediaUrl, onPlayerClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const containerRef = useRef(null);

  // Check club in URL to use different terrain image
  const currentUrl = window.location.href.toLowerCase();
  const terrainImage = currentUrl.includes('kansas') ? '/assets/terrainkansas.jpg'
    : currentUrl.includes('metropolitans') ? '/assets/terrainmetropolitans.jpg'
    : currentUrl.includes('byers') ? '/assets/terrainbyers.jpg'
    : '/assets/terrain.jpg';

  // Define all positions that should always be displayed
  const allPositions = ['Bases', 'Escoltas', 'Aleros', 'Ala-Pívots', 'Pívots'];

  const [displayedPlayers, setDisplayedPlayers] = useState(() => {
    const initial = {};
    // Initialize for all positions, not just those in boards
    allPositions.forEach((pos) => {
      initial[pos] = 3;
    });
    return initial;
  });

  const getInitialPositions = () => {
    const basePositions = isMobile ? {
      'Bases': { left: 50, top: 8 },
      'Escoltas': { left: 15, top: 28 },
      'Aleros': { left: 75, top: 48 },
      'Ala-Pívots': { left: 15, top: 68 },
      'Pívots': { left: 50, top: 85 }
    } : {
      'Bases': { left: 35, top: 65 },
      'Escoltas': { left: 65, top: 65 },
      'Aleros': { left: 25, top: 25 },
      'Ala-Pívots': { left: 75, top: 25 },
      'Pívots': { left: 50, top: 15 }
    };
    return basePositions;
  };

  const [positions, setPositions] = useState(() => {
    // Try localStorage first (shared across tabs), then sessionStorage (tab-specific)
    const storageKey = `court-positions-${boards?.[0]?.libelle || 'default'}`;
    const saved = localStorage.getItem(storageKey) || sessionStorage.getItem(storageKey);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return getInitialPositions();
      }
    }
    return getInitialPositions();
  });

  useEffect(() => {
    const storageKey = `court-positions-${boards?.[0]?.libelle || 'default'}`;
    const positionsJson = JSON.stringify(positions);
    // Save to both localStorage (shared) and sessionStorage (backup)
    localStorage.setItem(storageKey, positionsJson);
    sessionStorage.setItem(storageKey, positionsJson);
  }, [positions, boards]);

  const [, drop] = useDrop({
    accept: 'position-box',
    drop: (item, monitor) => {
      const delta = monitor.getDifferenceFromInitialOffset();
      const containerRect = containerRef.current?.getBoundingClientRect();
      
      if (delta && containerRect && item.initialOffset) {
        // Calculer la nouvelle position en tenant compte du décalage initial
        const newX = item.initialOffset.x + delta.x;
        const newY = item.initialOffset.y + delta.y;
        
        // Convertir en pourcentages en tenant compte de la taille de l'élément
        // pour que le centre de l'élément soit positionné correctement
        const xPercent = ((newX + item.width / 2) / containerRect.width) * 100;
        const yPercent = ((newY + item.height / 2) / containerRect.height) * 100;
        
        setPositions(prev => ({
          ...prev,
          [item.position]: { 
            left: Math.max(5, Math.min(95, xPercent)), 
            top: Math.max(5, Math.min(95, yPercent)) 
          }
        }));
      }
    },
  });

  drop(containerRef);

  const posMap = useMemo(() => {
    const map = new Map();
    boards.forEach((b) => map.set(b.libelle, b.players));
    return map;
  }, [boards]);
  
  const getPlayers = (pos) => {
    const allPlayers = posMap.get(pos) || [];
    const count = displayedPlayers[pos] || 3;
    return [...allPlayers]
      .sort((a, b) => a.classement - b.classement)
      .slice(0, count);
  };

  const canAddMore = (pos) => {
    const allPlayers = posMap.get(pos) || [];
    const currentCount = displayedPlayers[pos] || 3;
    return currentCount < allPlayers.length;
  };

  const canRemove = (pos) => {
    const currentCount = displayedPlayers[pos] || 3;
    return currentCount > 3;
  };

  const handleAddPlayer = (pos) => {
    setDisplayedPlayers(prev => ({
      ...prev,
      [pos]: Math.min((prev[pos] || 3) + 1, (posMap.get(pos) || []).length)
    }));
  };

  const handleRemovePlayer = (pos) => {
    setDisplayedPlayers(prev => ({
      ...prev,
      [pos]: Math.max((prev[pos] || 3) - 1, 3)
    }));
  };

  const handleMove = (newPositions) => {
    setPositions(newPositions);
  };

  useEffect(() => {
    const storageKey = `court-positions-${boards?.[0]?.libelle || 'default'}`;
    if (isMobile && !localStorage.getItem(storageKey) && !sessionStorage.getItem(storageKey)) {
      setPositions(getInitialPositions());
    }
  }, [isMobile, boards]);
  
  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        p: isMobile ? 0.5 : 1,
        background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)'
      }}
    >
      <Box sx={{ 
        position: 'relative', 
        width: '100%', 
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Box sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          backgroundImage: `url(${terrainImage})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#f5f5f5'
        }}>
          {allPositions.map((pos) => {
            const positionStyle = positions[pos] || { left: 50, top: 50 };

            return (
              <DraggableListBox
                key={pos}
                position={pos}
                players={getPlayers(pos)}
                sx={{
                  left: `${positionStyle.left}%`,
                  top: `${positionStyle.top}%`,
                  transform: 'translate(-50%, -50%)'
                }}
                onPlayerClick={onPlayerClick}
                onMove={handleMove}
                onAddPlayer={() => handleAddPlayer(pos)}
                onRemovePlayer={() => handleRemovePlayer(pos)}
                canAddMore={canAddMore(pos)}
                canRemove={canRemove(pos)}
                isMobile={isMobile}
                isDesktop={isDesktop}
                mediaUrl={mediaUrl}
                containerRef={containerRef}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

// PlayerDetailsDialog component commented out
/*
const PlayerDetailsDialog = ({ open, onClose, player, mediaUrl }) => {
  const navigate = useNavigate();

  if (!player) return null;

  const joinWithDash = (...items) => items.filter(Boolean).join(' - ');

  const basicInfo = joinWithDash(
    player.poste,
    player.annee_naissance,
    player.taille ? `${player.taille}cm` : null
  );

  const nationalityInfo = player.nationalite
    ? player.nationalite + (player.passport ? ` - (${player.passport})` : '')
    : '';

  // Build team info with competitions_detail if available, otherwise fallback to old fields
  let teamInfo = '';
  if (player.competitions_detail && player.competitions_detail.length > 0) {
    // Group competitions by team
    const equipesMap = new Map();
    player.competitions_detail.forEach(comp => {
      if (!equipesMap.has(comp.equipe_id)) {
        equipesMap.set(comp.equipe_id, {
          equipe_nom: comp.equipe_nom,
          competitions: []
        });
      }
      equipesMap.get(comp.equipe_id).competitions.push(comp.competition_nom);
    });

    // Format as: Team1 (Comp1, Comp2)
    teamInfo = Array.from(equipesMap.values())
      .map(equipe => `${equipe.equipe_nom} (${equipe.competitions.join(', ')})`)
      .join(' - ');
  } else {
    // Fallback to old fields
    teamInfo = joinWithDash(
      player.team,
      [player.league_1, player.league_2, player.competicion_europa]
        .filter(Boolean)
        .join(' - ')
    );
  }

  const handlePlayerNameClick = () => {
    window.open(`/players/${player.id}`, '_blank');
  };

  const getTrendIcon = (indicator) => {
    const lowerIndicator = indicator?.toLowerCase();
    if (lowerIndicator?.includes('high')) {
      return <TrendingUp color="success" />;
    } else if (lowerIndicator?.includes('low')) {
      return <TrendingDown color="error" />;
    } else if (lowerIndicator?.includes('stable')) {
      return <TrendingFlat color="action" />;
    }
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2
      }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
          Ficha Jugador
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {player.url && (
            <IconButton
              onClick={() => window.open(player.url, '_blank')}
              color="primary"
              sx={{
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'white'
                }
              }}
            >
              <OpenInNew />
            </IconButton>
          )}
          <IconButton
            onClick={onClose}
            sx={{
              '&:hover': {
                bgcolor: 'error.light',
                color: 'white'
              }
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
            <Avatar
              src={`${mediaUrl}/photos/${player.id}.png`}
              alt={player.nom}
              sx={{
                width: 100,
                height: 100,
                border: '3px solid',
                borderColor: 'primary.main',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
            />
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  mb: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'primary.main',
                    textDecoration: 'underline'
                  }
                }}
                onClick={handlePlayerNameClick}
              >
                {player.nom}
              </Typography>
              {player.classement && (
                <Chip
                  icon={<Trophy />}
                  label={`Clasificación: ${player.classement}`}
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            {basicInfo && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Height color="action" />
                  <Typography variant="body1" color="text.secondary">
                    {basicInfo}
                  </Typography>
                </Box>
              </Grid>
            )}

            {nationalityInfo && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Public color="action" />
                  <Typography variant="body1" color="text.secondary">
                    {nationalityInfo}
                  </Typography>
                </Box>
              </Grid>
            )}

            {teamInfo && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Groups color="action" />
                  <Typography variant="body1" color="text.secondary">
                    {teamInfo}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          {(player.forme_indicator || player.notes || player.stats) && (
            <>
              <Divider sx={{ my: 2 }} />

              <List>
                {player.forme_indicator && (
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>Forma</Typography>
                          {getTrendIcon(player.forme_indicator)}
                        </Box>
                      }
                      secondary={player.forme_indicator}
                    />
                  </ListItem>
                )}

                {player.notes && (
                  <ListItem>
                    <ListItemText
                      primary="Notes"
                      secondary={player.notes}
                    />
                  </ListItem>
                )}

                {player.stats && (
                  <ListItem>
                    <ListItemText
                      primary="Statistiques"
                      secondary={player.stats}
                    />
                  </ListItem>
                )}
              </List>
            </>
          )}
        </Paper>
      </DialogContent>
    </Dialog>
  );
};
*/

const BigBoardPitch = ({ open, onClose, boards, mediaUrl, tagName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const STORAGE_KEY = `bigboard-state-${tagName || 'default'}`;
  
  // Dialog-related state and functions commented out
  /*
  const getSavedState = () => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.selectedPlayer && boards) {
          const playerStillExists = boards.some(board =>
            board.players?.some(p => p.id === parsed.selectedPlayer.id)
          );
          if (!playerStillExists) {
            parsed.selectedPlayer = null;
            parsed.playerDialogOpen = false;
          }
        }
        return parsed;
      }
    } catch (e) {
      console.error('Error loading saved state:', e);
    }
    return { selectedPlayer: null, playerDialogOpen: false };
  };

  const [selectedPlayer, setSelectedPlayer] = useState(() => getSavedState().selectedPlayer);
  const [playerDialogOpen, setPlayerDialogOpen] = useState(() => getSavedState().playerDialogOpen);

  useEffect(() => {
    const state = { selectedPlayer, playerDialogOpen };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [selectedPlayer, playerDialogOpen, STORAGE_KEY]);

  useEffect(() => {
    if (!open) {
    }
  }, [open, STORAGE_KEY]);
  */

  const handlePlayerClick = (player) => {
    // Open player page in new tab instead of dialog
    window.open(`/players/${player.id}`, '_blank');
  };

  /*
  const handleClosePlayerDialog = () => {
    setPlayerDialogOpen(false);
  };
  */

  const Backend = isMobile ? TouchBackend : HTML5Backend;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '95vw',
            height: '95vh',
            maxWidth: '95vw',
            m: 0.5
          }
        }}
      >
        <DialogContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0,
            overflow: 'hidden',
            height: '100%',
            position: 'relative'
          }}
        >
          <IconButton 
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1000,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 1)',
              }
            }}
          >
            <Close />
          </IconButton>
          
          <DndProvider backend={Backend}>
            <BasketballCourt 
              boards={boards} 
              mediaUrl={mediaUrl} 
              onPlayerClick={handlePlayerClick}
            />
          </DndProvider>
        </DialogContent>
      </Dialog>

      {/* PlayerDetailsDialog commented out
      <PlayerDetailsDialog
        open={playerDialogOpen}
        onClose={handleClosePlayerDialog}
        player={selectedPlayer}
        mediaUrl={mediaUrl}
      />
      */}
    </>
  );
};

export default BigBoardPitch;